import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { 
  Home, 
  Waves, 
  Moon, 
  CreditCard, 
  Calendar, 
  HelpCircle,
  ChevronDown,
  Phone
} from "lucide-react";
import { useState, useEffect } from "react";

const faqCategories = [
  {
    id: "general",
    icon: HelpCircle,
    title: "General",
    faqs: [
      {
        question: "Where are you located?",
        answer: "Son's Guadalupe is located riverside on River Road in New Braunfels, TX. Our address is 7308 River Rd, New Braunfels, TX 78132. We are located about 7 miles from loop 337 or 1/2 mile past the second crossing on the right hand side near the Lone Star Float House. For help with booking or general questions call: 877-577-7667"
      },
      {
        question: "What are your hours of operation?",
        answer: "Our property is open from 10:30 AM to 6:30 PM for day guests. Glamping guests have prolonged access to their tents and the property amenities according to their rental agreement."
      },
      {
        question: "Is Son's Guadalupe family-friendly?",
        answer: "Absolutely! We've designed our property specifically for families. We enforce quiet hours, maintain a peaceful atmosphere, and have on-site staff to ensure everyone has a safe, enjoyable experience."
      },
      {
        question: "Do you have on-site staff?",
        answer: "Yes, we have friendly staff on-site during all operating hours to assist with check-in, answer questions, and ensure your experience is top-notch."
      },
      {
        question: "Is there parking available?",
        answer: "Yes! Every cabana and glamping reservation includes a dedicated parking spot close to your space. No shuttles or long walks required."
      },
      {
        question: "Do you have restrooms on the property?",
        answer: "Yes, we have clean, well-maintained restrooms and bathhouses located throughout the property for all guests to use."
      },
      {
        question: "Do you allow pets?",
        answer: "No, we love pets but unfortunately our insurance does not allow them."
      },
      {
        question: "What should I bring?",
        answer: "Here are some suggestions: Soap, shampoo, conditioner, paper towels, firewood, fishing poles/tackle/bait, lifejackets, charcoal/lighter fluid/matches, water shoes or rain boots, tubes/floats/noodles/rafts, camp chairs, bug spray, flashlight, and swimsuits."
      },
      {
        question: "Are there lifeguards on duty?",
        answer: "There are no lifeguards on duty, no fence or barrier restricting persons from entering the water and no depth signs. Underwater hazards may exist and swimming is at your own risk. Renter is responsible for the safety and well-being of all guests and is responsible for keeping a close watch on all swimmers as well as non-swimmers or persons who may enter or accidentally enter the water. Should the Renter/Responsible party be absent from the Property for any length of time, it is his/her duty to designate another person within his/her party for this responsibility."
      },
      {
        question: "Do you have Wi-Fi?",
        answer: "Yes! Son's Guadalupe has Wi-Fi on property so you can stay connected on vacation!"
      }
    ]
  },
  {
    id: "cabanas",
    icon: Home,
    title: "Cabanas",
    faqs: [
      {
        question: "What time can I arrive and leave for my cabana rental?",
        answer: "Daytime cabana rentals run from 10:30 AM to 6:30 PM. We recommend arriving early to maximize your river time!"
      },
      {
        question: "How many people can fit in a cabana?",
        answer: "Each cabana accommodates up to 8 guests maximum though up to 12 may be at any cabana."
      },
      {
        question: "What's included with my cabana rental?",
        answer: "Your cabana includes a covered shaded area with ceiling fans, picnic tables, a charcoal grill, dedicated parking, and private river access. Electricity and water are also available at each cabana."
      },
      {
        question: "Can I bring my own food and drinks?",
        answer: "Yes! Bring your own food, snacks, and beverages. Each cabana has a charcoal grill and picnic tables. Just remember — no glass containers allowed on the property."
      },
      {
        question: "Can we rent a cabana and then have a party or wedding at your property?",
        answer: "No, guest/renter may use the property as a private residence only. While we understand that you may have additional guests over, no parties, large groups or additional overnight guests are allowed exceeding the maximum occupancy. All guests must be registered and sign a liability waiver. Exceeding the maximum occupancy or having parties/large groups at the property will be reason for eviction without reimbursement of rents and deposits."
      },
      {
        question: "Can I decorate my cabana for a special occasion?",
        answer: "Yes, you're welcome to bring decorations for birthdays, celebrations, etc. We just ask that you remove all decorations before leaving and avoid anything that could damage the cabana structure."
      }
    ]
  },
  {
    id: "tubing",
    icon: Waves,
    title: "Tubing",
    faqs: [
      {
        question: "How long is the tubing float?",
        answer: "The float takes approximately 2-3 hours depending on water levels and how leisurely you take it. It's the perfect length to enjoy the scenery without getting too tired or sunburned."
      },
      {
        question: "Do I need to be a strong swimmer to go tubing?",
        answer: "No! The river is calm and shallow in most areas. Life jackets are available and required for non-swimmers. Children should always be accompanied by adults."
      },
      {
        question: "Can I bring my own tube?",
        answer: "Outside tubes are not permitted on our property. We provide high-quality, heavy-duty tubes for rent at $29.99 per person to ensure safety and a great experience for everyone."
      },
      {
        question: "What should I bring for tubing?",
        answer: "Sunscreen, sunglasses with a strap, water shoes or sandals that stay on, and a waterproof phone case. Skip the glass containers — cans and plastic only on the river."
      },
      {
        question: "Do you have tubes for kids?",
        answer: "Yes! We have tubes with and without bottoms."
      },
      {
        question: "Can I bring a cooler on the river?",
        answer: "Yes! We offer cooler tubes for $19.99 that float alongside you, keeping your drinks cold during the float."
      },
      {
        question: "Where does the tubing route end?",
        answer: "Right back at Son's Guadalupe! The best part — you float right back to your private cabana or picnic table (if you rented one)! No hiking back to a parking lot. Your shaded home base is waiting for you."
      }
    ]
  },
  {
    id: "glamping",
    icon: Moon,
    title: "Glamping",
    faqs: [
      {
        question: "What's the difference between glamping and regular camping?",
        answer: "Glamping combines the outdoor experience with comfortable amenities. Our tents feature foam mattress beds with air conditioning, fans, electricity, and more."
      },
      {
        question: "What time is check-in and check-out for glamping?",
        answer: "Check-in is at 3 PM and check-out is at 11 AM. Early check-in or late check-out may be available upon request, subject to availability."
      },
      {
        question: "How many people can stay in a glamping tent?",
        answer: "The maximum occupancy for glamping cabins is 5 guests."
      },
      {
        question: "Are there bathrooms inside the glamping tents?",
        answer: "Tents do not have private bathrooms. We have clean, well-maintained bathhouses located a short walk from all glamping sites with showers, restrooms, and sinks."
      },
      {
        question: "Can I cook inside the glamping tent?",
        answer: "Cooking inside tents is not permitted for safety reasons. However, you can bring a portable grill for outdoor cooking, and we have charcoal grills available. Many guests bring coolers with prepared foods and snacks."
      },
      {
        question: "Is glamping available year-round?",
        answer: "Yes! Our tents are equipped with both AC and heating, making them comfortable in Texas summers and mild winters. The river is beautiful in every season though we are closed in the winter months."
      },
      {
        question: "Do glamping guests have river access?",
        answer: "Absolutely! Glamping guests have full access to the river and can add tubing to their stay for $29.99 per person."
      }
    ]
  },
  {
    id: "bookings",
    icon: Calendar,
    title: "Reservations",
    faqs: [
      {
        question: "How do I make a reservation?",
        answer: "Reservations may be made online at www.SonsGuadalupe.com/book-now or by calling 877-577-7667. The Renter/Responsible party must be 25 years of age or older unless accompanied by an adult guardian or parent. Families are encouraged to stay."
      },
      {
        question: "How far in advance should I book?",
        answer: "We recommend booking as early as possible, especially for summer weekends which fill up quickly. We only have 22 cabanas, so availability is limited."
      },
      {
        question: "Can I modify my reservation?",
        answer: "Yes, you can modify your reservation by calling us at 877-577-7667. Changes are subject to availability and may be subject to price differences."
      },
      {
        question: "Do you require a deposit?",
        answer: "Yes, 50% is due at time of booking to make a reservation. The remaining balance is due 7 days before arrival."
      },
      {
        question: "Is there a minimum stay requirement for glamping?",
        answer: "There's no minimum stay during weekdays. Weekend reservations (Friday-Saturday) may have a 2-night minimum during peak season."
      }
    ]
  },
  {
    id: "policies",
    icon: CreditCard,
    title: "Policies & Payment",
    faqs: [
      {
        question: "What is your cancellation policy?",
        answer: "More than 15 days notice prior to the booking date: 90% refund or credit. Between 7 and 14 days prior to the booking date: 50% refund or credit. Less than 7 days notice prior to the booking date: No refund or credit. Same day reservation — no refund will be given (less than 7 days)."
      },
      {
        question: "What kind of payments do you accept?",
        answer: "We accept cash onsite as well as Visa, MasterCard and Discover credit cards. All payments should be made in US Dollars."
      },
      {
        question: "Do you require a waiver?",
        answer: "Yes, all guests are required to sign a liability waiver before participating in river activities. Waivers can be completed online before your arrival or on-site at check-in."
      },
      {
        question: "What happens if there's bad weather?",
        answer: "Light rain doesn't stop the fun — you're already getting wet! For severe weather (lightning, flash flooding), we'll pause operations for safety. If we cancel due to severe weather, you'll receive a full or partial refund depending at what time we cancel during the day."
      },
      {
        question: "Are glass containers allowed?",
        answer: "No, glass containers are not allowed anywhere on the property for safety reasons. Please bring cans, plastic bottles, or other non-glass containers."
      }
    ]
  }
];

const FAQs = () => {
  const [activeCategory, setActiveCategory] = useState("general");

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkfront-middleware-production.up.railway.app/chat-widget.js?property=guadalupe";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  const activeData = faqCategories.find(cat => cat.id === activeCategory);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="FAQs | Son's Guadalupe — Guadalupe River Cabanas & Tubing"
        description="Common questions about Son's Guadalupe including what to bring, parking, river conditions, cancellation policy, check-in times, and more for planning your Guadalupe River trip."
        canonical="/faqs"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-teal pt-40 pb-20 md:pt-48 md:pb-24">
        <div className="container-narrow mx-auto px-4 md:px-8 text-center">
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
            Everything you need to know before your Guadalupe River adventure
          </p>
        </div>
      </section>

      <main className="py-16">
        <div className="container-narrow mx-auto px-4 md:px-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {faqCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                  activeCategory === category.id
                    ? "bg-teal text-white shadow-soft"
                    : "bg-cream text-charcoal hover:bg-teal/10"
                }`}
              >
                <category.icon className="w-4 h-4" />
                {category.title}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-elevated p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                {activeData && (
                  <>
                    <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
                      <activeData.icon className="w-5 h-5 text-teal" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-charcoal">
                      {activeData.title} Questions
                    </h2>
                  </>
                )}
              </div>
              
              <div className="space-y-4">
                {activeData?.faqs.map((faq, index) => (
                  <details 
                    key={index}
                    className="group bg-cream rounded-xl cursor-pointer"
                  >
                    <summary className="flex items-center justify-between p-5 font-heading font-bold text-charcoal list-none">
                      <span className="pr-4">{faq.question}</span>
                      <ChevronDown className="w-5 h-5 text-teal flex-shrink-0 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-charcoal/70 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* Still Have Questions */}
          <div className="max-w-3xl mx-auto mt-12">
            <div className="bg-cream rounded-2xl p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-charcoal mb-4">
                Still Have Questions?
              </h3>
              <p className="text-charcoal/70 mb-6">
                Our friendly team is here to help. Give us a call or send us a message.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="default" size="lg" className="gap-2">
                  <Phone className="w-4 h-4" />
                  Call 877-577-7667
                </Button>
                <Button variant="outline" size="lg">
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQs;
